// Avisos do Assistente IA (pedido 2026-10-21): pra cada tipo de aviso o
// cliente escolhe Notificação no app e/ou WhatsApp. Abre pela engrenagem
// da aba Assistente. Salva sozinho a cada mudança (sem popup de
// confirmação); erro aparece na própria tela.
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bell, Check, Loader2, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  gpsDriveAiAssistantService,
  type AiNotificationSettings,
  type AiNotifyType,
} from "@/services/gpsdrive-ai-assistant.service";

const KEY = ["ai-assistant", "notification-settings"] as const;
const ICON: Record<AiNotifyType, string> = {
  confirmations: "✅",
  agenda_reminder: "⏰",
  agenda_daily: "📅",
  expenses_weekly: "💰",
  expenses_monthly: "📊",
};

const errMsg = (e: unknown) => (e instanceof GpsDriveApiError || e instanceof Error ? e.message : "Erro inesperado");

export function AiNotificationSettingsScreen({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: () => gpsDriveAiAssistantService.notificationSettings() });
  const [form, setForm] = useState<AiNotificationSettings | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (query.data && !form) setForm(query.data);
  }, [query.data, form]);

  const persist = (next: AiNotificationSettings, delay = 0) => {
    setForm(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setStatus("saving");
      setError(null);
      try {
        const saved = await gpsDriveAiAssistantService.updateNotificationSettings({
          whatsappPhone: next.whatsappPhone.trim() || null,
          dailyTime: next.dailyTime,
          prefs: next.prefs,
        });
        qc.setQueryData(KEY, saved);
        setStatus("saved");
      } catch (e) {
        setError(errMsg(e));
        setStatus("idle");
      }
    }, delay);
  };

  const toggle = (type: AiNotifyType, channel: "push" | "whatsapp") => {
    if (!form) return;
    persist({ ...form, prefs: { ...form.prefs, [type]: { ...form.prefs[type], [channel]: !form.prefs[type][channel] } } });
  };

  const test = async (channel: "push" | "whatsapp") => {
    setTestMsg(null);
    try {
      await gpsDriveAiAssistantService.testNotification(channel);
      setTestMsg(channel === "push" ? "Notificação de teste enviada." : "WhatsApp de teste enviado.");
    } catch (e) {
      setTestMsg(errMsg(e));
    }
  };

  const anyWhatsapp = form ? Object.values(form.prefs).some((c) => c.whatsapp) : false;

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-slate-100 dark:bg-slate-950">
      <header
        className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 pb-2 dark:border-slate-800 dark:bg-slate-900"
        style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          aria-label="Voltar"
          onClick={onClose}
          className="grid h-10 w-10 place-items-center rounded-full text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="font-semibold text-slate-900 dark:text-white">Avisos do assistente</div>
          <div className="text-xs text-slate-500">Escolha onde receber cada aviso</div>
        </div>
        <span className="text-xs text-slate-500">
          {status === "saving" && <Loader2 className="h-4 w-4 animate-spin" />}
          {status === "saved" && (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <Check className="h-3.5 w-3.5" /> Salvo
            </span>
          )}
        </span>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}>
        {!form && (query.error ? <p className="text-sm text-red-600">{errMsg(query.error)}</p> : <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-400" />)}
        {error && <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</p>}

        {form?.types.map((t) => (
          <div key={t.key} className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
            <div className="flex items-start gap-3">
              <span className="text-xl leading-none">{ICON[t.key]}</span>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 dark:text-white">{t.title}</div>
                <div className="text-xs text-slate-500">{t.description}</div>
              </div>
            </div>
            {t.key === "agenda_daily" && (
              <label className="mt-3 flex items-center justify-between gap-3 text-sm text-slate-700 dark:text-slate-200">
                Horário do resumo
                <input
                  type="time"
                  value={form.dailyTime}
                  onChange={(e) => e.target.value && persist({ ...form, dailyTime: e.target.value }, 600)}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 dark:border-slate-700 dark:bg-slate-800"
                />
              </label>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <ChannelButton active={form.prefs[t.key].push} onClick={() => toggle(t.key, "push")} icon={<Bell className="h-4 w-4" />} label="Notificação" />
              <ChannelButton
                active={form.prefs[t.key].whatsapp}
                onClick={() => toggle(t.key, "whatsapp")}
                icon={<MessageCircle className="h-4 w-4" />}
                label="WhatsApp"
                disabled={!form.whatsappConnected}
              />
            </div>
          </div>
        ))}

        {form && (
          <div className="space-y-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
            <div className="font-medium text-slate-900 dark:text-white">Seu WhatsApp</div>
            {!form.whatsappConnected ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                O envio por WhatsApp ainda não está disponível. Por enquanto, use as notificações do app.
              </p>
            ) : (
              <>
                <input
                  type="tel"
                  inputMode="tel"
                  placeholder="(00) 00000-0000"
                  value={form.whatsappPhone}
                  onChange={(e) => persist({ ...form, whatsappPhone: e.target.value }, 900)}
                  className={cn(
                    "w-full rounded-xl border bg-slate-50 px-3 py-2.5 text-[15px] dark:bg-slate-800",
                    anyWhatsapp && form.whatsappPhone.replace(/\D/g, "").length < 10
                      ? "border-red-300"
                      : "border-slate-200 dark:border-slate-700",
                  )}
                />
                <p className="text-xs text-slate-500">Com DDD. As mensagens chegam pelo WhatsApp da empresa.</p>
              </>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <button type="button" onClick={() => test("push")} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200">
                Testar notificação
              </button>
              {form.whatsappConnected && (
                <button type="button" onClick={() => test("whatsapp")} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200">
                  Testar WhatsApp
                </button>
              )}
            </div>
            {testMsg && <p className="text-xs text-slate-600 dark:text-slate-300">{testMsg}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function ChannelButton({
  active,
  onClick,
  icon,
  label,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-sm font-medium transition-colors disabled:opacity-40",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-slate-200 text-slate-500 dark:border-slate-700 dark:text-slate-400",
      )}
    >
      {icon}
      {label}
      {active && <Check className="h-3.5 w-3.5" />}
    </button>
  );
}
